import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons'
import { GraviButton, GraviGrid, Horizontal, MaterialIcon, Texto, Vertical } from '@gravitate-js/excalibrr'
import type { CellClassParams, GridApi, ICellRendererParams, IFilter } from 'ag-grid-community'
import { Form, InputNumber, Popover, Tooltip } from 'antd'
import type { MutableRefObject } from 'react'
import type { EditableMileageBandsGridProps, MileageBand } from '../types'
import { useEditableMileageBandsGrid } from '../hooks'

export function EditableMileageBandsGrid({
  rateType,
  dirtyGridApiRef,
  editingCopyRows,
  setEditingCopyRows,
  onHasDuplicatesChange,
  onHasInvalidMileValuesChange,
}: EditableMileageBandsGridProps) {
  const {
    gridApiRef,
    popoverForm,
    duplicateMiles,
    lastRowEditPopoverVisible,
    editingNodeId,
    handleAddMileageBand,
    handleSaveLastToMile,
    handleCancelLastToMile,
    handleGridReady,
    handleRowDataUpdated,
    getColumnDefs,
    setLastRowEditPopoverVisible,
    setEditingNodeId,
  } = useEditableMileageBandsGrid({
    rateType,
    dirtyGridApiRef,
    editingCopyRows,
    setEditingCopyRows,
    onHasDuplicatesChange,
    onHasInvalidMileValuesChange,
  })

  // Custom cell renderers
  const milesColumnRenderer = (params: CellClassParams<MileageBand>) => {
    if (!params.data || !params.api) return null

    const isLastRow = params.rowIndex === params.api.getDisplayedRowCount() - 1
    const isDuplicate = params.data.from_mile !== undefined && duplicateMiles.has(params.data.from_mile)

    // Base content
    const milesContent = (
      <Horizontal verticalCenter gap={4} style={{ flexGrow: 1 }}>
        {params.value === undefined ? (
          <Texto appearance='hint' style={{ fontStyle: 'italic' }}>
            Enter Miles
          </Texto>
        ) : (
          <Texto>{params.value}</Texto>
        )}
        <Texto appearance='hint' style={{ display: 'flex', alignItems: 'center' }}>
          <MaterialIcon icon='keyboard_arrow_right' className='pr-1' />
          {params.data.to_mile === undefined ? (
            <Texto style={{ fontStyle: 'italic' }}>(N/A)</Texto>
          ) : params.data.to_mile === null ? (
            <Texto style={{ fontStyle: 'italic' }}>(N/A)</Texto>
          ) : (
            params.data.to_mile
          )}
        </Texto>
        {isDuplicate && (
          <Tooltip title="Duplicate 'From Miles' value">
            <MaterialIcon icon='error' style={{ color: 'var(--theme-error)', marginLeft: 4 }} size={16} />
          </Tooltip>
        )}
      </Horizontal>
    )

    // Conditional Edit Button for the last row
    const editButton = isLastRow ? (
      <Tooltip title="Edit 'to' mile">
        <GraviButton
          size='small'
          appearance='text'
          icon={<EditOutlined />}
          onClick={(e) => {
            e.stopPropagation()
            popoverForm.setFieldsValue({ to_mile: params.data?.to_mile })
            setEditingNodeId(params.node.id ?? null)
            setLastRowEditPopoverVisible(true)
          }}
          style={{ marginLeft: 'auto' }}
        />
      </Tooltip>
    ) : null

    return (
      <Horizontal verticalCenter flex={1} style={{ width: '100%', justifyContent: 'space-between' }}>
        {milesContent}
        {editButton}
      </Horizontal>
    )
  }

  const actionsColumnRenderer = (params: ICellRendererParams<MileageBand>) => {
    return (
      <GraviButton
        error
        onClick={() => {
          if (params.data?.key) {
            const key = params.data.key
            const handleDelete = () => {
              const rowDataToRemove = editingCopyRows.find((band) => band.key === key)
              if (rowDataToRemove && gridApiRef.current) {
                gridApiRef.current.applyTransaction({
                  remove: [rowDataToRemove],
                })
              }
            }
            handleDelete()
          }
        }}
        icon={<DeleteOutlined />}
        size='small'
      />
    )
  }

  // Override column definitions with custom renderers
  const columnDefs = getColumnDefs().map((colDef) => {
    if (colDef.field === 'from_mile') {
      return {
        ...colDef,
        cellRenderer: milesColumnRenderer,
      }
    }

    if (colDef.field === 'actions') {
      return {
        ...colDef,
        cellRenderer: actionsColumnRenderer,
      }
    }

    return colDef
  })

  return (
    <Vertical flex={1}>
      <GraviGrid<MileageBand, IFilter>
        rowData={editingCopyRows}
        columnDefs={columnDefs}
        externalRef={gridApiRef as MutableRefObject<GridApi<MileageBand>>}
        agPropOverrides={{
          getRowId: (params) => params.data.key ?? '',
          rowGroupPanelShow: 'never',
          rowHeight: 40,
          onGridReady: handleGridReady,
          onRowDataUpdated: handleRowDataUpdated,
        }}
        storageKey='ratebook-mileage-bands-edit'
        // @ts-ignore
        dirtyChangesRef={dirtyGridApiRef}
        isDirtyEdit
        hideDirtyToolbar
        controlBarProps={{
          actionButtons: (
            <Tooltip title='Add Mileage Band'>
              <GraviButton theme2 appearance='outlined' onClick={handleAddMileageBand} icon={<PlusOutlined />} />
            </Tooltip>
          ),
          title: 'Mileage Bands',
          customSearchBar: <div />,
        }}
      />

      {/* Popover (moved out of cell renderer for cleaner separation) */}
      <Popover
        open={lastRowEditPopoverVisible && !!editingNodeId}
        onOpenChange={(visible) => {
          if (!visible && editingNodeId) {
            handleCancelLastToMile()
            popoverForm.resetFields()
          }
        }}
        trigger='click'
        title="Edit 'To' Mile"
        content={
          <Form form={popoverForm} layout='vertical' onFinish={handleSaveLastToMile}>
            <Vertical gap={8} style={{ minWidth: 180 }}>
              <Form.Item
                name='to_mile'
                noStyle
                rules={[
                  { required: true, message: 'To Mile is required' },
                  {
                    validator: (_, value) => {
                      // The node ID has data that includes from_mile
                      const node = gridApiRef.current?.getRowNode(editingNodeId || '')
                      const fromMile = node?.data?.from_mile

                      if (value === null || value === undefined || fromMile === undefined || fromMile === null) {
                        // Should be caught by required rule, but handle just in case
                        return Promise.resolve()
                      }
                      if (value < fromMile) {
                        return Promise.reject(new Error('Must be >= From Mile'))
                      }
                      return Promise.resolve()
                    },
                  },
                ]}
              >
                <InputNumber min={0} style={{ width: '100%' }} placeholder='Enter end mile' />
              </Form.Item>
              <Horizontal justifyContent='flex-end' gap={8}>
                <GraviButton size='small' appearance='outlined' onClick={handleCancelLastToMile}>
                  Cancel
                </GraviButton>
                <GraviButton size='small' theme2 appearance='solid' htmlType='submit'>
                  Save
                </GraviButton>
              </Horizontal>
            </Vertical>
          </Form>
        }
      />
    </Vertical>
  )
}
